import { type RefObject, useCallback, useEffect, useRef } from 'react';
import { isInteractiveElement } from '@bynder-studio/misc';
import { isCtrlKey } from 'packages/helpers/hotKeys';
import { millisecondToFrames } from 'packages/common/editor/editorHelper';
import type { SimplePlayerHandle } from 'packages/common/player/Simple';

type UseTrimShortcutsParams = {
    playerRef: RefObject<SimplePlayerHandle | null>;
    offsetTime: number;
    elementDuration: number;
    fullDuration: number;
    frameRate: number;
    isSlipMode: boolean;
    onTrimChange: (offsetFrame: number, durationFrame: number | null, forceNewEntry?: boolean) => void;
    onUndo: () => void;
    onRedo: () => void;
    correctStartFrame: (frame: number) => number;
    correctEndFrame: (frame: number) => number;
};

/**
 * Hook that provides keyboard shortcuts for the video/audio trim mode.
 *
 * Shortcuts:
 * - Space:              Play/pause
 * - ArrowRight:         Move playhead 1 frame forward
 * - ArrowLeft:          Move playhead 1 frame backward
 * - Shift+ArrowRight:   Move playhead 10 frames forward
 * - Shift+ArrowLeft:    Move playhead 10 frames backward
 * - Cmd+Shift+ArrowLeft:  Skip to start of trimmed clip
 * - Cmd+Shift+ArrowRight: Skip to end of trimmed clip
 * - I:                  Set in-point (start) at current playhead
 * - O:                  Set out-point (end) at current playhead
 * - Cmd+Z:             Undo
 * - Cmd+Shift+Z:       Redo
 */
const useTrimShortcuts = ({
    playerRef,
    offsetTime,
    elementDuration,
    fullDuration,
    frameRate,
    isSlipMode,
    onTrimChange,
    onUndo,
    onRedo,
    correctStartFrame,
    correctEndFrame,
}: UseTrimShortcutsParams) => {
    const offsetTimeRef = useRef(offsetTime);
    const elementDurationRef = useRef(elementDuration);
    const fullDurationRef = useRef(fullDuration);
    const isSlipModeRef = useRef(isSlipMode);
    const onTrimChangeRef = useRef(onTrimChange);
    const onUndoRef = useRef(onUndo);
    const onRedoRef = useRef(onRedo);
    const correctStartFrameRef = useRef(correctStartFrame);
    const correctEndFrameRef = useRef(correctEndFrame);

    useEffect(() => {
        offsetTimeRef.current = offsetTime;
    }, [offsetTime]);

    useEffect(() => {
        elementDurationRef.current = elementDuration;
    }, [elementDuration]);

    useEffect(() => {
        fullDurationRef.current = fullDuration;
    }, [fullDuration]);

    useEffect(() => {
        isSlipModeRef.current = isSlipMode;
    }, [isSlipMode]);

    useEffect(() => {
        onTrimChangeRef.current = onTrimChange;
    }, [onTrimChange]);

    useEffect(() => {
        onUndoRef.current = onUndo;
    }, [onUndo]);

    useEffect(() => {
        onRedoRef.current = onRedo;
    }, [onRedo]);

    useEffect(() => {
        correctStartFrameRef.current = correctStartFrame;
    }, [correctStartFrame]);

    useEffect(() => {
        correctEndFrameRef.current = correctEndFrame;
    }, [correctEndFrame]);

    const togglePlayback = useCallback(() => {
        const player = playerRef.current;

        if (!player) {
            return;
        }

        if (player.isPaused()) {
            player.play();
        } else {
            player.pause();
        }
    }, [playerRef]);

    const seekByFrames = useCallback(
        (frameCount: number) => {
            const player = playerRef.current;

            if (!player) {
                return;
            }

            const frameDurationSec = 1 / frameRate;
            const currentTime = player.getCurrentTime();
            const newTime = currentTime + frameCount * frameDurationSec;

            const maxTimeSec = fullDurationRef.current / 1000;

            const clampedTime = Math.max(0, Math.min(newTime, maxTimeSec));
            player.setCurrentTime(clampedTime);

            if (!player.isPaused()) {
                player.pause();
            }
        },
        [playerRef, frameRate],
    );

    const skipToTrimStart = useCallback(() => {
        const player = playerRef.current;

        if (!player) {
            return;
        }

        player.setCurrentTime(offsetTimeRef.current / 1000);
    }, [playerRef]);

    const skipToTrimEnd = useCallback(() => {
        const player = playerRef.current;

        if (!player) {
            return;
        }

        // Skip to the last frame of the trimmed clip (1 frame before the end boundary)
        const endTimeSec = (offsetTimeRef.current + elementDurationRef.current) / 1000;
        const lastFrameSec = endTimeSec - 1 / frameRate;
        player.setCurrentTime(Math.max(offsetTimeRef.current / 1000, lastFrameSec));
    }, [playerRef, frameRate]);

    const setInPoint = useCallback(() => {
        const player = playerRef.current;

        if (!player) {
            return;
        }

        const currentTimeMs = player.getCurrentTime() * 1000;
        const currentFrame = millisecondToFrames(currentTimeMs, frameRate);
        const correctedStart = correctStartFrameRef.current(currentFrame);

        if (isSlipModeRef.current) {
            onTrimChangeRef.current(correctedStart, null, true);
        } else {
            const currentEndTimeMs = offsetTimeRef.current + elementDurationRef.current;
            const endFrame = millisecondToFrames(currentEndTimeMs, frameRate);
            const newDuration = endFrame - correctedStart;
            onTrimChangeRef.current(correctedStart, newDuration, true);
        }
    }, [playerRef, frameRate]);

    const setOutPoint = useCallback(() => {
        const player = playerRef.current;

        if (!player) {
            return;
        }

        const currentTimeMs = player.getCurrentTime() * 1000;
        const currentFrame = millisecondToFrames(currentTimeMs, frameRate);
        const correctedEnd = correctEndFrameRef.current(currentFrame);

        if (isSlipModeRef.current) {
            const durationInFrames = millisecondToFrames(elementDurationRef.current, frameRate);
            const newStartFrame = Math.max(0, correctedEnd - durationInFrames);
            onTrimChangeRef.current(newStartFrame, null, true);
        } else {
            const startFrame = millisecondToFrames(offsetTimeRef.current, frameRate);
            const newDuration = correctedEnd - startFrame;
            onTrimChangeRef.current(startFrame, newDuration, true);
        }
    }, [playerRef, frameRate]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (isInteractiveElement(event)) {
                return;
            }

            // Space key — toggle playback
            if (event.code === 'Space') {
                event.preventDefault();
                togglePlayback();

                return;
            }

            // Undo: Cmd+Z (without Shift)
            if (isCtrlKey(event) && event.code === 'KeyZ' && !event.shiftKey) {
                event.preventDefault();
                event.stopPropagation();
                onUndoRef.current();

                return;
            }

            // Redo: Cmd+Shift+Z
            if (isCtrlKey(event) && event.code === 'KeyZ' && event.shiftKey) {
                event.preventDefault();
                event.stopPropagation();
                onRedoRef.current();

                return;
            }

            // Arrow keys — frame navigation
            if (event.code === 'ArrowLeft') {
                event.preventDefault();

                if (isCtrlKey(event) && event.shiftKey) {
                    // Cmd+Shift+Left: Skip to start of trimmed clip
                    skipToTrimStart();
                } else if (!isCtrlKey(event)) {
                    // Left or Shift+Left: back 1 or 10 frames
                    seekByFrames(event.shiftKey ? -10 : -1);
                }

                return;
            }

            if (event.code === 'ArrowRight') {
                event.preventDefault();

                if (isCtrlKey(event) && event.shiftKey) {
                    // Cmd+Shift+Right: Skip to end of trimmed clip
                    skipToTrimEnd();
                } else if (!isCtrlKey(event)) {
                    // Right or Shift+Right: forward 1 or 10 frames
                    seekByFrames(event.shiftKey ? 10 : 1);
                }

                return;
            }

            // I key — set in-point at current playhead
            if (event.code === 'KeyI' && !isCtrlKey(event) && !event.shiftKey && !event.altKey) {
                event.preventDefault();
                setInPoint();

                return;
            }

            // O key — set out-point at current playhead
            if (event.code === 'KeyO' && !isCtrlKey(event) && !event.shiftKey && !event.altKey) {
                event.preventDefault();
                setOutPoint();
            }
        },
        [togglePlayback, seekByFrames, skipToTrimStart, skipToTrimEnd, setInPoint, setOutPoint],
    );

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);
};

export default useTrimShortcuts;
